import { Link } from 'react-router-dom';
import { mediaAsset } from '../config/media';

const sharedStages = [
  ['01', 'Ingest', 'Curated RSS registry, feed health checks, and full-article extraction'],
  ['02', 'Edit', 'Topic balancing, source caps, duplicate detection, and correspondent routing'],
  ['03', 'Write', 'LLM summaries, multi-source synthesis, handoffs, reactions, and attribution'],
];

const productionPipelines = [
  {
    id: 'radio',
    number: 'A',
    title: 'Radio',
    purpose: 'Continuous synthetic broadcast',
    description: 'The production path designed to run all day: fast, cacheable, bandwidth-efficient, and already faster than real time.',
    stages: ['Multi-voice Kokoro TTS', 'Music beds + station IDs', 'Category-aware stingers', 'Fixed audio crossfades', 'Loudness normalization', 'MP3 assembly'],
    benchmark: '26.5 minutes rendered in 15m 45s · 0.594× real time',
  },
  {
    id: 'television',
    number: 'B',
    title: 'Television',
    purpose: 'Generated visual reports',
    description: 'The selective, GPU-intensive path for complete episodes, social clips, installation visuals, and promotional material.',
    stages: ['Shared multi-voice narration', 'SDXL topical imagery', 'MuseTalk presenter animation', 'Broadcast layout + lower thirds', 'Captions + transitions', 'FFmpeg MP4 assembly'],
    benchmark: 'Initial local baseline · approximately 11.3× slower than playback',
  },
];

const problems = [
  {
    title: 'A feed is not an article',
    problem: 'RSS summaries are short, inconsistent, and often point to galleries, paywalls, or malformed pages.',
    solution: 'The pipeline validates each destination, extracts full text, rejects teasers and non-articles, and records why every candidate was accepted or discarded.',
  },
  {
    title: 'Random selection creates bad programming',
    problem: 'Unbounded randomness clusters stories by topic and lets a single publisher dominate a rundown.',
    solution: 'An assignment layer enforces source and topic caps, identifies related coverage, and synthesizes multiple sources when stories overlap.',
  },
  {
    title: 'Synthetic speech needs radio structure',
    problem: 'A sequence of generated voice files lacks the pacing, transitions, and continuity of a coherent radio program.',
    solution: 'Typed script metadata drives handoffs, desk reactions, station IDs, category-aware bumpers, fixed crossfades, and a measured FFmpeg timeline.',
  },
  {
    title: 'GPU acceleration should be optional',
    problem: 'The development machine, remote CUDA worker, and cloud renderer have different capabilities and failure modes.',
    solution: 'TTS and video jobs use explicit worker boundaries, validated result manifests, deterministic ordering, and local fallbacks when a remote worker is unavailable.',
  },
];

function ArchitectureDiagram() {
  return (
    <div className="news-study__system-map" role="img" aria-label="A shared editorial core branches into separate radio and television production pipelines">
      <div className="news-study__shared-core">
        <p className="portfolio-eyebrow">Shared editorial core</p>
        <div className="news-study__architecture">
          {sharedStages.map(([number, title, description], index) => (
            <div className="news-study__stage" key={title}>
              <span>{number}</span>
              <h3>{title}</h3>
              <p>{description}</p>
              {index < sharedStages.length - 1 && <b aria-hidden="true">→</b>}
            </div>
          ))}
        </div>
      </div>
      <div className="news-study__branch-marker" aria-hidden="true"><span>Shared editorial state</span></div>
      <div className="news-study__pipeline-branches">
        {productionPipelines.map((pipeline) => (
          <article className={`news-study__pipeline news-study__pipeline--${pipeline.id}`} key={pipeline.id}>
            <div className="news-study__pipeline-heading">
              <span>{pipeline.number}</span>
              <div>
                <p className="portfolio-eyebrow">{pipeline.purpose}</p>
                <h3>{pipeline.title}</h3>
              </div>
            </div>
            <p>{pipeline.description}</p>
            <ol>
              {pipeline.stages.map((stage) => <li key={stage}>{stage}</li>)}
            </ol>
            <p className="news-study__pipeline-benchmark">{pipeline.benchmark}</p>
          </article>
        ))}
      </div>
    </div>
  );
}

export default function NewsPipelineCaseStudy() {
  return (
    <main id="main-content" className="portfolio-page news-study">
      <header className="news-study__intro">
        <p className="portfolio-eyebrow">Fake News · Autonomous media systems · 2026</p>
        <h1>An autonomous, multi-agent synthetic news broadcast system.</h1>
        <p className="news-study__lede">
          A production pipeline that turns validated reporting into structured radio and television programs. The system coordinates editorial policy, language-model writing, multi-voice synthesis, GPU workers, audio imaging, and deterministic media assembly into a single production pipeline.
        </p>
        <div className="news-study__tags" aria-label="Core technologies">
          {['Python', 'LLMs', 'Kokoro TTS', 'CUDA workers', 'FFmpeg', 'RSS'].map((tag) => <span key={tag}>{tag}</span>)}
        </div>
      </header>

      <section className="news-study__reel" aria-labelledby="reel-heading">
        <div className="news-study__section-label">
          <p className="portfolio-eyebrow">00:69 · Highlight reel</p>
          <h2 id="reel-heading">The system on air</h2>
        </div>
        <video controls preload="metadata" poster={mediaAsset('media/news-pipeline/reel-poster.jpg')}>
          <source src={mediaAsset('media/news-pipeline/highlight-reel.mp4')} type="video/mp4" />
          Your browser does not support embedded video.
        </video>
        <p className="news-study__caption">Synthetic anchor performance, generated editorial imagery, lower thirds, station imaging, and automated assembly.</p>
      </section>

      <section className="news-study__section" aria-labelledby="architecture-heading">
        <div className="news-study__section-label">
          <p className="portfolio-eyebrow">System architecture</p>
          <h2 id="architecture-heading">One editorial core, two production pipelines</h2>
        </div>
        <ArchitectureDiagram />
      </section>

      <section className="news-study__section" aria-labelledby="problems-heading">
        <div className="news-study__section-label">
          <p className="portfolio-eyebrow">Engineering decisions</p>
          <h2 id="problems-heading">Engineering a reliable path from source to broadcast</h2>
        </div>
        <div className="news-study__problems">
          {problems.map((item, index) => (
            <article key={item.title}>
              <span>0{index + 1}</span>
              <h3>{item.title}</h3>
              <p><strong>Problem.</strong> {item.problem}</p>
              <p><strong>Solution.</strong> {item.solution}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="news-study__metrics" aria-labelledby="benchmarks-heading">
        <div className="news-study__section-label">
          <p className="portfolio-eyebrow">Measured + modeled</p>
          <h2 id="benchmarks-heading">Performance and cost</h2>
        </div>
        <div className="news-study__metric-grid">
          <article><strong>26.5 min</strong><span>finished radio program</span></article>
          <article><strong>15m 45s</strong><span>end-to-end generation</span></article>
          <article><strong>0.594×</strong><span>real-time render factor</span></article>
          <article><strong>12 / 6 / 7</strong><span>stories / sources / topic groups</span></article>
        </div>
        <div className="news-study__benchmark-notes">
          <p><strong>Measured locally.</strong> Feed acquisition took 4.5 seconds, language-model work 12 minutes 11 seconds, and TTS plus audio assembly 3 minutes 29 seconds. The output rendered 40% faster than playback.</p>
          <p><strong>Video baseline.</strong> The first Intel Mac Pro / Vega II implementation rendered roughly 11.3 times slower than playback. Worker isolation and CUDA migration define the path to real-time video.</p>
          <p><strong>Modeled cloud cost.</strong> An optimized 30-minute RTX 5090 render is estimated at $0.27–$0.45 in GPU time. At one episode per day, compute remains approximately $8–$14 per month before storage and orchestration.</p>
        </div>
      </section>

      <section className="news-study__section news-study__section--safeguards" aria-labelledby="safeguards-heading">
        <div className="news-study__section-label">
          <p className="portfolio-eyebrow">Editorial controls</p>
          <h2 id="safeguards-heading">Attribution is part of the data model</h2>
        </div>
        <div className="news-study__safeguards">
          <ul>
            <li>Only validated full articles enter the writing stage.</li>
            <li>Every story retains feed URL, article URL, publisher, topic, and extraction result.</li>
            <li>Source and topic caps prevent accidental editorial monopolies.</li>
            <li>Related reporting can be synthesized while preserving every contributing source.</li>
            <li>Source attribution moves between desk handoff and correspondent handback, but is never omitted.</li>
            <li>Serious subjects receive restrained reactions; unsupported numbers are rejected.</li>
          </ul>
          <aside>
            <p className="portfolio-eyebrow">Disclaimer</p>
            <h3>Synthetic satire</h3>
            <p>This project is an experimental, synthetic satire. Its hosts, correspondents, voices, commentary, images, and broadcast presentation are generated. It is not a news organization and should not be used as a primary source of information.</p>
          </aside>
        </div>
      </section>

      <section className="news-study__programs" aria-labelledby="programs-heading">
        <div className="news-study__section-label">
          <p className="portfolio-eyebrow">Pipeline outputs</p>
          <h2 id="programs-heading">Continuous radio, selective television</h2>
        </div>
        <article className="news-study__program news-study__program--radio">
          <div>
            <p className="portfolio-eyebrow">Pipeline A · Radio · 26:30</p>
            <h3>Continuous synthetic broadcast</h3>
            <p>The viable 24-hour service layer. A twelve-story benchmark sourced from BBC News, CBS News, CNBC, ESPN, NPR, and The Guardian, with generated station imaging and ten standards-audited desk reactions.</p>
          </div>
          <audio controls preload="metadata">
            <source src={mediaAsset('media/news-pipeline/radio-program.mp3')} type="audio/mpeg" />
          </audio>
        </article>
        <article className="news-study__program news-study__program--television">
          <div>
            <p className="portfolio-eyebrow">Pipeline B · Television · 06:34</p>
            <h3>Generated visual reports</h3>
            <p>The selective social and promotional layer. Three reports assembled into a continuous branded episode with presenter animation, topical imagery, captions, and transitions.</p>
          </div>
          <a className="portfolio-button" href={mediaAsset('media/news-pipeline/complete-episode.mp4')}>Open complete episode</a>
        </article>
      </section>

      <section className="news-study__art" aria-labelledby="art-heading">
        <p className="portfolio-eyebrow">Media-art experiment</p>
        <h2 id="art-heading">When the newsroom becomes an instrument</h2>
        <p>Digital audio workstations moved the recording studio into the computer by turning recording, editing, mixing, and processing into software. <cite>Fake News</cite> applies a similar transformation to broadcast production, bringing source selection, writing, voice performance, visual generation, and program assembly into one automated system. Once those functions become software, the same reporting can be reordered, personalized, scored, and staged for a particular listener or object.</p>
        <p>The discomfort at the center of the work comes from its uncanny approximation of broadcast authority, and from how cheaply that authority can now be simulated. Familiar broadcast gestures can make provisional language feel settled and institutional. The project uses satire, visible attribution, and deliberately synthetic presentation to ask where authorship sits when selection, writing, performance, and distribution are delegated to machines.</p>
      </section>

      <div className="case-study-next news-study__back">
        <p className="portfolio-eyebrow">Lab navigation</p>
        <Link to="/lab">Return to experiments <span aria-hidden="true">→</span></Link>
      </div>
    </main>
  );
}
